import React from 'react';
import autoBind from 'react-autobind';
import axios from 'axios';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  TextField,
  Box,
  LinearProgress,
  Card,
  Chip,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import ImageIcon from '@material-ui/icons/Image';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import AddPhotoAlternateIcon from '@material-ui/icons/AddPhotoAlternate';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import Localization from '../Localization';
import Config from '../Config.jsx';

// ---------------------------------------------------------------------------
// AZURE gpt-image-2 migration (2026-07).
// image_360_generate returns the image as base64 for preview. On "apply" the
// base64 is persisted through the normal upload.php path (same as any file
// upload) -> file_id -> onUploadSuccess -> set_stage_media_file, then the AI 360
// photo quota is redeemed.
// (The old Blockade Labs generate->poll->download flow + StyleSelectionDialog are gone.)
//
// 2026-08: the server no longer holds one connection open for the ~2 minutes the
// render takes — that idle connection was being dropped by some school/corporate
// networks. It answers immediately and renders in the background, and we poll
// until the image is ready. A poll reply carries pending:true; a genuine Azure
// per-minute rate limit does not. Both set rate_limited so that an older client
// keeps polling, so pending is what distinguishes "your image is rendering" from
// "the service is busy" — they must not show the same message.
// ---------------------------------------------------------------------------

const styles = theme => ({
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  dialogPaper: {
    minWidth: 800,
    minHeight: 600,
    display: 'flex',
    flexDirection: 'column',
  },
  dialogContent: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  errorIcon: {
    width: 128,
    height: 128,
    color: theme.palette.grey[400],
    margin: theme.spacing(2, 0),
  },
  previewImage: {
    width: '100%',
    display: 'block',
    borderRadius: 4,
  },
  progressBox: {
    width: '90%',
    margin: '0 auto',
    textAlign: 'center',
  },
  progressIcon: {
    width: 150,
    height: 150,
    color: '#DDD',
    margin: theme.spacing(2, 0),
  },
  sectionLabel: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: 500,
  },
  styleChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  styleChip: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  refRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  refThumbWrap: {
    position: 'relative',
    width: 72,
    height: 72,
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  refThumb: {
    width: 72,
    height: 72,
    objectFit: 'cover',
    borderRadius: 4,
    border: '1px solid ' + theme.palette.grey[300],
  },
  refRemove: {
    position: 'absolute',
    top: -10,
    right: -10,
    color: theme.palette.grey[700],
    backgroundColor: 'white',
    padding: 0,
  },
});

// Timing: a 4K high-quality gpt-image-2 render was measured at ~2 minutes.
const ESTIMATED_GENERATION_TIME = 120 * 1000; // 120s — drives the estimated progress bar
const GENERATION_TIMEOUT = 240 * 1000;        // 240s — hard client-side timeout

const MAX_RATE_LIMIT_WAIT_MS = 15 * 60 * 1000; // keep auto-retrying up to 15 min while rate-limited (429)
const MAX_RATE_LIMIT_RETRIES = 100;            // hard safety cap on the number of retries
const MAX_REFERENCE_IMAGES = 4;
const REFERENCE_MAX_DIM = 1024; // downscale reference images before sending (they are only conditioning)
const GENERATION_SIZE = '3840x1920'; // 4K 2:1

// Style presets (map to backend prompt suffixes in core/api_ai.php: ai_style_suffix()).
const STYLE_PRESETS = [
  { key: 'photorealistic', name: 'Photorealistic' },
  { key: 'cinematic',      name: 'Cinematic' },
  { key: 'anime',          name: 'Anime / Manga' },
  { key: 'cartoon',        name: 'Cartoon' },
  { key: 'fantasy',        name: 'Fantasy' },
  { key: 'historical',     name: 'Historical' },
];

let lastSelectedStyle = 'photorealistic';

class AIGenerateDialog extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      open: false,
      prompt: '',
      style: lastSelectedStyle,
      referenceImages: [], // { name, dataUrl, b64 }

      generationStatus: 'idle', // idle, progress, complete, error
      progress: 0,
      generatedB64: null,
      generatedCost: null,
      errorMessage: '',
      progressTimer: null,
      progressInterval: null,

      // Why we are waiting before the next poll:
      //   'pending' = the server is still rendering our image (normal, ~2 min)
      //   'busy'    = Azure's per-minute cap; nothing has started rendering yet
      //   null      = not waiting
      waitReason: null,
      retryAttempt: 0,
      retryInSeconds: 0,
      retryTimeout: null,
      retryCountdownInterval: null,

      // Apply (persist to stage) states
      uploadStatus: 'idle', // idle, uploading, complete, error
      uploadProgress: 0,
      uploadErrorMessage: '',
      uploadTimer: null,
      uploadInterval: null,
      uploadedFileId: null,
    };
    autoBind(this);
  }

  handleOpen() {
    this.clearTimers();
    this.setState({
      open: true,
      prompt: '',
      style: lastSelectedStyle,
      referenceImages: [],
      generationStatus: 'idle',
      progress: 0,
      generatedB64: null,
      generatedCost: null,
      errorMessage: '',
      waitReason: null,
      retryAttempt: 0,
      retryInSeconds: 0,
      uploadStatus: 'idle',
      uploadProgress: 0,
      uploadErrorMessage: '',
      uploadedFileId: null,
    });
  }

  handleClose() {
    this.setState({ open: false });
    this.clearTimers();
  }

  clearTimers() {
    if (this.state.progressTimer) clearTimeout(this.state.progressTimer);
    if (this.state.progressInterval) clearInterval(this.state.progressInterval);
    this.clearRetryTimers();
    this.clearUploadTimers();
  }

  // Only the between-poll timers. The progress bar has to survive a poll — clearing it here would
  // restart the bar from zero every few seconds while the server is still rendering.
  clearRetryTimers() {
    if (this.state.retryTimeout) clearTimeout(this.state.retryTimeout);
    if (this.state.retryCountdownInterval) clearInterval(this.state.retryCountdownInterval);
  }

  clearUploadTimers() {
    if (this.state.uploadTimer) clearTimeout(this.state.uploadTimer);
    if (this.state.uploadInterval) clearInterval(this.state.uploadInterval);
  }

  // ---- Style ----
  handleStyleSelect(styleKey) {
    this.setState({ style: styleKey });
    lastSelectedStyle = styleKey;
  }

  // ---- Reference images (optional, up to 4) ----
  handleReferenceFilesSelected(event) {
    const files = Array.from((event.target && event.target.files) || []);
    const remaining = MAX_REFERENCE_IMAGES - this.state.referenceImages.length;
    const toAdd = files.slice(0, remaining);
    if (event.target) event.target.value = ''; // allow re-selecting the same file

    Promise.all(toAdd.map(this.loadDownscaledImage))
      .then(imgs => {
        const valid = imgs.filter(Boolean);
        this.setState({ referenceImages: this.state.referenceImages.concat(valid) });
      })
      .catch(err => console.log('reference image load failed', err));
  }

  loadDownscaledImage(file) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const scale = Math.min(1, REFERENCE_MAX_DIM / Math.max(img.width, img.height));
          const w = Math.max(1, Math.round(img.width * scale));
          const h = Math.max(1, Math.round(img.height * scale));
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          canvas.getContext('2d').drawImage(img, 0, 0, w, h);
          const dataUrl = canvas.toDataURL('image/png');
          resolve({ name: file.name, dataUrl, b64: dataUrl.split(',')[1] });
        };
        img.onerror = () => resolve(null);
        img.src = e.target.result;
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    });
  }

  removeReferenceImage(index) {
    const next = this.state.referenceImages.slice();
    next.splice(index, 1);
    this.setState({ referenceImages: next });
  }

  // ---- Generation ----
  handleGenerateClick() {
    if (!this.state.prompt) {
      this.setState({ errorMessage: Localization.strings.AI_PROMPT_EMPTY_ERROR });
      return;
    }

    const requestObj = {
      prompt: this.state.prompt,
      style: this.state.style,
      size: GENERATION_SIZE,
    };
    const refs = this.state.referenceImages.map(r => r.b64).filter(Boolean);
    if (refs.length > 0) {
      requestObj.reference_images = refs;
    }
    this.generationRequest = requestObj; // reused verbatim by every poll, so the server can match
                                         // them to the one job it is already rendering
    this.rateLimitDeadline = Date.now() + MAX_RATE_LIMIT_WAIT_MS; // stop retrying after this
    this.generationStartTime = Date.now(); // paces the whole generation, not the current request

    this.setState({
      generationStatus: 'progress', progress: 0, errorMessage: '', generatedB64: null,
      waitReason: null, retryAttempt: 0,
    });
    this.startProgressBar();
    this.fireGenerate();
  }

  // Start the generation, or poll for the render already running on the server.
  fireGenerate() {
    if (this.state.retryCountdownInterval) clearInterval(this.state.retryCountdownInterval);
    // progress is deliberately NOT reset here: this runs every few seconds and the bar tracks the
    // whole generation.
    this.setState({ waitReason: null, retryCountdownInterval: null });
    this.props.root.refs.API.request('image_360_generate', this.generationRequest, this.generateOK, this.generateFailed);
    this.startRequestTimeout();
  }

  generateOK(res) {
    const result = res && res.body ? res.body.result : null;

    // Come back later. Two different situations share this shape, because the server sets
    // rate_limited on both so an older client keeps polling:
    //   pending:true -> our image is still rendering on the server (the normal path)
    //   otherwise    -> Azure's per-minute request cap; nothing is rendering yet
    if (result && result.rate_limited) {
      // started:true marks the poll on which the server actually began rendering — that is when the
      // ~2 minute estimate should start counting, since a real rate limit can delay the start.
      if (result.started === true) {
        this.generationStartTime = Date.now();
        this.setState({ progress: 0 });
      }
      this.scheduleRetry(result.retry_after, result.pending === true);
      return;
    }

    if (result && result.success && result.image_b64) {
      // Cost is for admin/debug only — logged, not shown in the UI.
      if (result.cost) {
        console.log('[AI 360] cost estimate (USD):', result.cost.usd, result.cost, 'usage:', result.usage);
      }
      this.clearTimers();
      this.setState({
        generationStatus: 'complete',
        generatedB64: result.image_b64,
        generatedCost: result.cost || null,
        progress: 100,
      });
    } else {
      this.clearTimers();
      this.setState({
        generationStatus: 'error',
        errorMessage: (result && result.error) || Localization.strings.AI_GENERATION_START_FAILED,
      });
    }
  }

  generateFailed(err) {
    this.clearTimers();
    this.setState({ generationStatus: 'error', errorMessage: Localization.strings.AI_API_REQUEST_FAILED });
  }

  // Wait, then ask again. isPending distinguishes "the server is rendering our image" from "the
  // service is busy" — the wait is mechanically identical, but they must not read the same to the
  // person watching, and only the busy case is worth mentioning at all.
  scheduleRetry(retryAfter, isPending) {
    this.clearRetryTimers(); // NB: not clearTimers() — the progress bar spans the whole generation

    const attempt = this.state.retryAttempt + 1;

    let waitSec = parseInt(retryAfter, 10);
    if (isNaN(waitSec) || waitSec <= 0) waitSec = 30;
    waitSec += Math.floor(Math.random() * 6); // jitter so multiple clients don't poll in lockstep

    // Give up once the 15-min budget is exhausted (or the safety retry cap is hit).
    const deadline = this.rateLimitDeadline || (Date.now() + MAX_RATE_LIMIT_WAIT_MS);
    if (attempt > MAX_RATE_LIMIT_RETRIES || (Date.now() + waitSec * 1000) > deadline) {
      this.clearTimers();
      this.setState({
        generationStatus: 'error',
        waitReason: null,
        errorMessage: isPending
          ? Localization.strings.AI_GENERATION_TOO_SLOW
          : Localization.strings.AI_SERVICE_BUSY_TIMEOUT,
      });
      return;
    }

    const retryTimeout = setTimeout(() => { this.fireGenerate(); }, waitSec * 1000);
    const retryCountdownInterval = setInterval(() => {
      const next = this.state.retryInSeconds - 1;
      this.setState({ retryInSeconds: next > 0 ? next : 0 });
    }, 1000);

    this.setState({
      generationStatus: 'progress',
      waitReason: isPending ? 'pending' : 'busy',
      retryAttempt: attempt,
      retryInSeconds: waitSec,
      retryTimeout,
      retryCountdownInterval,
    });
  }

  // Estimated progress for the WHOLE generation. Started once and left running across polls, so the
  // bar keeps climbing while the server renders instead of restarting on every request.
  startProgressBar() {
    if (this.state.progressInterval) clearInterval(this.state.progressInterval);

    const progressInterval = setInterval(() => {
      if (this.state.generationStatus !== 'progress') {
        clearInterval(progressInterval);
        return;
      }
      const elapsedTime = Date.now() - this.generationStartTime;
      let currentProgress = this.state.progress;
      if (elapsedTime < ESTIMATED_GENERATION_TIME) {
        const targetProgress = (elapsedTime / ESTIMATED_GENERATION_TIME) * 95;
        currentProgress += (targetProgress - currentProgress) * 0.1;
      } else if (currentProgress < 99) {
        currentProgress += 0.1;
      }
      this.setState({ progress: Math.min(currentProgress, 99) });
    }, 200);

    this.setState({ progressInterval });
  }

  // Guard on a SINGLE request. Each call is short now — the server answers straight away and renders
  // in the background — so a request still open after this long means something upstream is holding
  // the connection. Restarted on every poll; the overall cap is the deadline in scheduleRetry().
  startRequestTimeout() {
    if (this.state.progressTimer) clearTimeout(this.state.progressTimer);

    const progressTimer = setTimeout(() => {
      if (this.state.generationStatus === 'progress') {
        this.clearTimers();
        this.setState({ generationStatus: 'error', errorMessage: Localization.strings.AI_GENERATION_TIMEOUT });
      }
    }, GENERATION_TIMEOUT);

    this.setState({ progressTimer });
  }

  // ---- Apply to stage: persist the generated image through the normal upload.php path ----
  handleApplyClick() {
    const { root, stageData, localeData } = this.props;

    const userId = root.getMyUserId();
    const localeId = (localeData ? localeData.id : null) || (stageData ? stageData.locale_id : null);
    if (!userId || !localeId) {
      this.applyFailed(`${Localization.strings.AI_USER_LOCALE_ID_ERROR} UserID: ${userId}, LocaleID: ${localeId}`);
      return;
    }

    let file;
    try {
      file = this.base64ToFile(this.state.generatedB64, 'ai_360_' + Date.now() + '.png', 'image/png');
    } catch (e) {
      this.applyFailed(Localization.strings.AI_UPLOAD_INVALID_RESPONSE);
      return;
    }

    this.setState({ uploadStatus: 'uploading', uploadProgress: 0, uploadErrorMessage: '' });

    const formData = new FormData();
    formData.append('selectedFile', file, file.name);

    const self = this;
    axios.post(Config.UPLOAD_PATH(), formData, {
      params: { locale_id: localeId, user_id: userId },
      onUploadProgress: function (progressEvent) {
        if (progressEvent.total) {
          const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          // Cap at 95% until the server confirms; the response completes it.
          self.setState({ uploadProgress: Math.min(pct, 95) });
        }
      },
    })
      .then(function (response) { self.applyUploadOK(response); })
      .catch(function (error) {
        let detail = Localization.strings.AI_UPLOAD_API_FAILED;
        if (error && error.response) {
          console.log('[AI 360] upload error — HTTP', error.response.status, error.response.data);
          detail += ' (HTTP ' + error.response.status + ')';
          if (typeof error.response.data === 'string' && error.response.data.trim().length > 0) {
            detail += ': ' + error.response.data.substring(0, 300);
          }
        } else {
          console.log('[AI 360] upload error', error);
          if (error && error.message) detail += ' (' + error.message + ')';
        }
        self.applyFailed(detail);
      });
  }

  applyUploadOK(response) {
    const data = response && response.data ? response.data : {};
    if (data.status === 'success' && data.file_id) {
      const fileId = data.file_id;
      this.setState({ uploadStatus: 'complete', uploadedFileId: fileId, uploadProgress: 100 });
      this.redeemAI360PhotoQuota(fileId);

      if (this.props.onUploadSuccess) {
        this.props.onUploadSuccess({ file_id: fileId });
      }
      setTimeout(() => { this.handleClose(); }, 500); // let 100% show briefly
    } else {
      // Surface the raw server reply so an unexpected upload response is diagnosable.
      console.log('[AI 360] unexpected upload response — status:', response && response.status, 'data:', response && response.data);
      let detail = Localization.strings.AI_UPLOAD_INVALID_RESPONSE;
      if (data && data.message) {
        detail = data.message;
      } else if (typeof data === 'string' && data.trim().length > 0) {
        detail += ' — ' + data.substring(0, 300);
      }
      this.applyFailed(detail);
    }
  }

  applyFailed(err) {
    const errorMessage = typeof err === 'string' ? err : Localization.strings.AI_UPLOAD_API_FAILED;
    this.setState({ uploadStatus: 'error', uploadErrorMessage: errorMessage });
    this.clearUploadTimers();
  }

  redeemAI360PhotoQuota(fileId) {
    const { root } = this.props;
    const userId = root.getMyUserId();
    if (!userId) return;
    root.refs.API.request(
      'redeem_ai_360_photo_quota',
      { user_id: userId, file_id: fileId },
      this.redeemAI360PhotoQuotaOK,
      this.redeemAI360PhotoQuotaFailed
    );
  }

  redeemAI360PhotoQuotaOK(res) { /* quota redeemed */ }

  redeemAI360PhotoQuotaFailed(err) {
    console.log('redeem ai 360 photo quota failed ' + err);
  }

  base64ToFile(b64, filename, mime) {
    const byteChars = atob(b64);
    const len = byteChars.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = byteChars.charCodeAt(i);
    }
    return new File([bytes], filename, { type: mime });
  }

  // ---- Render ----
  renderIdle() {
    const { classes } = this.props;
    const canAddRef = this.state.referenceImages.length < MAX_REFERENCE_IMAGES;

    return (
      <React.Fragment>
        <DialogContent dividers className={classes.dialogContent}>
          <TextField
            autoFocus
            margin="dense"
            id="prompt"
            label={Localization.strings.AI_PROMPT_LABEL}
            placeholder={Localization.strings.AI_PROMPT_PLACEHOLDER}
            type="text"
            fullWidth
            multiline
            rowsMax={4}
            variant="outlined"
            value={this.state.prompt}
            onChange={(e) => this.setState({ prompt: e.target.value })}
            error={!!this.state.errorMessage}
            helperText={this.state.errorMessage}
          />

          <Typography variant="body2" className={classes.sectionLabel}>
            {Localization.strings.AI_STYLE_LABEL}
          </Typography>
          <Box className={classes.styleChips}>
            {STYLE_PRESETS.map(preset => (
              <Chip
                key={preset.key}
                label={preset.name}
                clickable
                className={classes.styleChip}
                color={this.state.style === preset.key ? 'primary' : 'default'}
                variant={this.state.style === preset.key ? 'default' : 'outlined'}
                onClick={() => this.handleStyleSelect(preset.key)}
              />
            ))}
          </Box>

          <Typography variant="body2" className={classes.sectionLabel}>
            Reference images (optional, up to {MAX_REFERENCE_IMAGES})
          </Typography>
          <Box className={classes.refRow}>
            {this.state.referenceImages.map((ref, idx) => (
              <div key={idx} className={classes.refThumbWrap}>
                <img src={ref.dataUrl} alt={ref.name} className={classes.refThumb} />
                <IconButton
                  size="small"
                  className={classes.refRemove}
                  onClick={() => this.removeReferenceImage(idx)}
                >
                  <HighlightOffIcon fontSize="small" />
                </IconButton>
              </div>
            ))}
            {canAddRef && (
              <Button
                variant="outlined"
                component="label"
                startIcon={<AddPhotoAlternateIcon />}
              >
                Add
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  multiple
                  hidden
                  onChange={this.handleReferenceFilesSelected}
                />
              </Button>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={this.handleClose} color="primary">
            {Localization.strings.CANCEL}
          </Button>
          <Button onClick={this.handleGenerateClick} color="primary" variant="contained">
            {Localization.strings.AI_GENERATE_BUTTON}
          </Button>
        </DialogActions>
      </React.Fragment>
    );
  }

  renderProgress() {
    const { classes } = this.props;

    // Azure's per-minute cap: nothing is rendering yet, so say that plainly.
    if (this.state.waitReason === 'busy') {
      return (
        <React.Fragment>
          <DialogContent dividers className={classes.dialogContent}>
            <Box className={classes.progressBox}>
              <Typography gutterBottom>
                {Localization.strings.AI_SERVICE_BUSY.replace('{sec}', this.state.retryInSeconds)}
              </Typography>
              <ImageIcon className={classes.progressIcon} />
              <LinearProgress />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleClose} color="primary">
              {Localization.strings.CANCEL}
            </Button>
          </DialogActions>
        </React.Fragment>
      );
    }

    // Everything else, including while the server renders in the background: from the waiting
    // person's point of view this is simply "generating", so it gets the ordinary progress bar.
    return (
      <DialogContent dividers className={classes.dialogContent}>
        <Box className={classes.progressBox}>
          <Typography gutterBottom>{Localization.strings.AI_GENERATING_MESSAGE}</Typography>
          <ImageIcon className={classes.progressIcon} />
          <LinearProgress variant="determinate" value={this.state.progress} />
          <Typography variant="body2" color="textSecondary">{`${Math.round(this.state.progress)}%`}</Typography>
          <Typography variant="body2" color="textSecondary">{Localization.strings.AI_GENERATING_HINT}</Typography>
          {this.state.errorMessage && <Typography color="error">{this.state.errorMessage}</Typography>}
        </Box>
      </DialogContent>
    );
  }

  renderComplete() {
    const { classes } = this.props;
    const previewSrc = this.state.generatedB64 ? ('data:image/png;base64,' + this.state.generatedB64) : '';
    return (
      <React.Fragment>
        <DialogContent dividers className={classes.dialogContent}>
          <Typography gutterBottom>{Localization.strings.AI_GENERATION_COMPLETE}</Typography>
          <Card>
            <img
              src={previewSrc}
              alt={Localization.strings.AI_GENERATED_THUMBNAIL_TITLE}
              className={classes.previewImage}
            />
          </Card>
        </DialogContent>
        <DialogActions>
          <Button onClick={this.handleClose} color="secondary">
            {Localization.strings.DISCARD}
          </Button>
          <Button onClick={this.handleApplyClick} color="primary" variant="contained">
            {Localization.strings.AI_APPLY_TO_STAGE}
          </Button>
        </DialogActions>
      </React.Fragment>
    );
  }

  renderError() {
    const { classes } = this.props;
    return (
      <React.Fragment>
        <DialogContent dividers className={classes.dialogContent}>
          <Box textAlign="center">
            <Typography variant="h5" color="error" gutterBottom>
              {Localization.strings.AI_ERROR_TITLE}
            </Typography>
            <ErrorOutlineIcon className={classes.errorIcon} />
            <Typography>{this.state.errorMessage}</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={this.handleClose} color="primary">
            {Localization.strings.CLOSE}
          </Button>
        </DialogActions>
      </React.Fragment>
    );
  }

  renderUploadProgress() {
    const { classes } = this.props;
    return (
      <Dialog
        open={this.state.uploadStatus === 'uploading' || this.state.uploadStatus === 'error'}
        onClose={() => { if (this.state.uploadStatus === 'error') this.setState({ uploadStatus: 'idle' }); }}
        disableBackdropClick
        disableEscapeKeyDown
      >
        <DialogTitle>{Localization.strings.AI_UPLOADING_TO_STAGE}</DialogTitle>
        <DialogContent>
          {this.state.uploadStatus === 'uploading' &&
            <Box className={classes.progressBox} style={{ width: 400 }}>
              <Typography gutterBottom>{Localization.strings.AI_UPLOAD_MESSAGE}</Typography>
              <LinearProgress variant="determinate" value={this.state.uploadProgress} />
              <Typography variant="body2" color="textSecondary">{`${Math.round(this.state.uploadProgress)}%`}</Typography>
            </Box>
          }
          {this.state.uploadStatus === 'error' &&
            <Typography color="error">{this.state.uploadErrorMessage}</Typography>
          }
        </DialogContent>
        <DialogActions>
          {this.state.uploadStatus === 'error' &&
            <Button onClick={() => this.setState({ uploadStatus: 'idle' })} color="primary">
              {Localization.strings.CLOSE}
            </Button>
          }
        </DialogActions>
      </Dialog>
    );
  }

  render() {
    const { classes } = this.props;
    let content;
    switch (this.state.generationStatus) {
      case 'progress': content = this.renderProgress(); break;
      case 'complete': content = this.renderComplete(); break;
      case 'error':    content = this.renderError(); break;
      case 'idle':
      default:         content = this.renderIdle(); break;
    }

    return (
      <React.Fragment>
        <Dialog
          open={this.state.open}
          onClose={this.handleClose}
          aria-labelledby="ai-generate-dialog-title"
          classes={{ paper: classes.dialogPaper }}
          disableBackdropClick={this.state.generationStatus === 'progress'}
          disableEscapeKeyDown={this.state.generationStatus === 'progress'}
        >
          <DialogTitle id="ai-generate-dialog-title">
            <Typography variant="h6">{Localization.strings.AI_GENERATE_DIALOG_TITLE}</Typography>
            {this.state.generationStatus !== 'progress' &&
              <IconButton aria-label="close" className={classes.closeButton} onClick={this.handleClose}>
                <CloseIcon />
              </IconButton>
            }
          </DialogTitle>
          {content}
        </Dialog>
        {this.renderUploadProgress()}
      </React.Fragment>
    );
  }
}

export default withStyles(styles)(AIGenerateDialog);
